import type {
  PerfilQuestionDto,
} from '@/features/perfilamiento/services/perfilamiento.types'

const STORAGE_KEY = 'app-sgapex-web:perfilamiento'

type PerfilamientoProcessSnapshot = {
  filterQuestion?: PerfilQuestionDto | null
  questionnaireQuestions?: PerfilQuestionDto[] | null
}

type PerfilamientoStore = Map<string, PerfilamientoProcessSnapshot>
type SerializedStoreEntry = [string, PerfilamientoProcessSnapshot]

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isProcessSnapshot(value: unknown): value is PerfilamientoProcessSnapshot {
  return isRecord(value)
}

function isSerializedStoreEntry(value: unknown): value is SerializedStoreEntry {
  return Array.isArray(value)
    && value.length === 2
    && typeof value[0] === 'string'
    && isProcessSnapshot(value[1])
}

function readStore(): PerfilamientoStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)

    if (!raw) return new Map()

    const parsed: unknown = JSON.parse(raw)

    if (Array.isArray(parsed)) {
      return new Map(parsed.filter(isSerializedStoreEntry))
    }

    if (isRecord(parsed)) {
      const legacyEntries = Object.entries(parsed)
        .filter((entry): entry is SerializedStoreEntry => isProcessSnapshot(entry[1]))

      return new Map(legacyEntries)
    }

    return new Map()
  } catch {
    return new Map()
  }
}

function writeStore(store: PerfilamientoStore) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...store.entries()]))
  } catch {
    // Ignore storage failures; the flow still works while the tab is open.
  }
}

export function persistPerfilamientoFilterQuestion(
  processId: string,
  filterQuestion: PerfilQuestionDto,
) {
  const store = readStore()
  store.set(processId, {
    ...store.get(processId),
    filterQuestion,
  })
  writeStore(store)
}

export function loadPerfilamientoFilterQuestion(processId: string) {
  return readStore().get(processId)?.filterQuestion ?? null
}

export function persistPerfilamientoQuestionnaireQuestions(
  processId: string,
  questionnaireQuestions: PerfilQuestionDto[],
) {
  const store = readStore()
  store.set(processId, {
    ...store.get(processId),
    questionnaireQuestions,
  })
  writeStore(store)
}

export function loadPerfilamientoQuestionnaireQuestions(processId: string) {
  return readStore().get(processId)?.questionnaireQuestions ?? null
}

export function clearPerfilamientoProcessState(processId: string) {
  const store = readStore()

  if (!store.has(processId)) return

  store.delete(processId)
  writeStore(store)
}
